import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { REPORT_DOWNLOAD_STATUS_POLL_INTERVAL_MS } from '@/config/constants'
import { requireAuthToken } from '@/lib/api'
import { credentialKeys } from '@/lib/queryKeys'
import { fetchReportDownloadStatus, runReportDownload } from './api'
import type { ReportType } from './types'

export function useReportDownloadStatusQuery(
  reportType: ReportType,
  enabled: boolean
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: credentialKeys.reportStatus(organization?.id, reportType),
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      return fetchReportDownloadStatus(token, reportType, signal)
    },
    enabled: enabled && !!organization?.id,
    refetchInterval: (query) =>
      query.state.data?.state === 'pending' ||
      query.state.data?.state === 'running'
        ? REPORT_DOWNLOAD_STATUS_POLL_INTERVAL_MS
        : false,
    retry: false,
  })
}

export function useRunReportDownloadMutation(reportType: ReportType) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const token = await requireAuthToken(getToken)
      return runReportDownload(token, reportType)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        credentialKeys.reportStatus(organization?.id, reportType),
        data
      )
    },
  })
}
